import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuthContext } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function PaymentCheck() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allPayments, setAllPayments] = useState<any[]>([]);
  const [pendingPayments, setPendingPayments] = useState<any[]>([]);

  useEffect(() => {
    if (user?.email !== 'viitamint@gmail.com') {
      navigate('/');
      return;
    }
    loadPayments();
  }, [user, navigate]);

  const loadPayments = async () => {
    console.log('🔍 Loading all subscription payments...');
    setLoading(true);
    
    try {
      const snapshot = await getDocs(collection(db, 'subscriptionPayments'));
      console.log('📊 Total documents:', snapshot.size);
      
      const all = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || null
      }));
      
      const pending = all.filter(p => p.status === 'pending');
      
      console.log('✅ All payments:', all);
      console.log('✅ Pending payments:', pending);
      
      setAllPayments(all);
      setPendingPayments(pending);
    } catch (error) {
      console.error('❌ Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Payment Check</h1>
        
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Summary</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 p-4 rounded">
              <p className="text-sm text-gray-600">Total Payments</p>
              <p className="text-3xl font-bold text-blue-600">{allPayments.length}</p>
            </div>
            <div className="bg-orange-50 p-4 rounded">
              <p className="text-sm text-gray-600">Pending Payments</p>
              <p className="text-3xl font-bold text-orange-600">{pendingPayments.length}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4">Loading...</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-xl font-bold mb-4">All Payments ({allPayments.length})</h2>
              {allPayments.length === 0 ? (
                <p className="text-gray-500">No payments found</p>
              ) : (
                <div className="space-y-4">
                  {allPayments.map(payment => (
                    <div key={payment.id} className="border rounded p-4">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><strong>ID:</strong> {payment.id}</div>
                        <div><strong>Status:</strong> <span className={`px-2 py-1 rounded ${
                          payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>{payment.status}</span></div>
                        <div><strong>Email:</strong> {payment.email || payment.instructorEmail || 'N/A'}</div>
                        <div><strong>Amount:</strong> ฿{payment.amount}</div>
                        <div><strong>Plan:</strong> {payment.plan || payment.tier || 'N/A'}</div>
                        <div><strong>Created:</strong> {payment.createdAt?.toLocaleString('th-TH') || 'N/A'}</div>
                      </div>
                      {payment.slipImageUrl && (
                        <div className="mt-2">
                          <img src={payment.slipImageUrl} alt="Slip" className="w-32 h-auto rounded" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-bold mb-4">Pending Payments ({pendingPayments.length})</h2>
              {pendingPayments.length === 0 ? (
                <p className="text-gray-500">No pending payments</p>
              ) : (
                <div className="space-y-4">
                  {pendingPayments.map(payment => (
                    <div key={payment.id} className="border-2 border-orange-300 rounded p-4 bg-orange-50">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div><strong>ID:</strong> {payment.id}</div>
                        <div><strong>Email:</strong> {payment.email || payment.instructorEmail || 'N/A'}</div>
                        <div><strong>Amount:</strong> ฿{payment.amount}</div>
                        <div><strong>Plan:</strong> {payment.plan || payment.tier || 'N/A'}</div>
                        <div><strong>Created:</strong> {payment.createdAt?.toLocaleString('th-TH') || 'N/A'}</div>
                      </div>
                      {payment.slipImageUrl && (
                        <div className="mt-2">
                          <img src={payment.slipImageUrl} alt="Slip" className="w-48 h-auto rounded" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        <button
          onClick={loadPayments}
          className="mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>
    </div>
  );
}
